import './ReactAuthorableModal.scss'
import { cloneElement, useRef, useState } from 'react'

import ModalHeader from '@cox/core-ui8/dist//ModalHeader'
import Fallback from '@cox/core-ui8/dist/Fallback'
import Modal, { ModalTypes } from '@cox/core-ui8/dist/Modal'
import ModalBody from '@cox/core-ui8/dist/ModalBody'
import ModalFooter from '@cox/core-ui8/dist/ModalFooter'
import { useUdoContext } from '@cox/core-ui8/dist/UdoContext'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'
import useLobExperience from '@cox/core-ui8/dist/useLobExperience'

import { ReactAuthorableModalProps } from './types'
import withAEM from '../../../hoc/withAEM'
import { DynamicMediaComponents, getDynamicMedia } from '../../../utils/dynamic-media-util'
import { clearLocalStorage, setLocalStorage } from '../../../utils/local-storage-util'
import { triggerNewRelicAction } from '../../../utils/newrelic-util'
import { getNestedValue, getTokenValues } from '../../../utils/rule-builder-util'

const ReactAuthorableModal = (props: ReactAuthorableModalProps) => {
  const {
    modalId = '',
    show,
    componentName = '',
    description,
    tokenProperties,
    pmrbtnText = '',
    pmrbtnLink = '',
    sndbtnText = '',
    sndbtnLink = '',
    children,
    modalType = '',
    isInEditor = false,
    pmrbtnClick,
    sndbtnClick,
    handleClose,
    ruleExpression = '',
    isWidget = false,
    fileName = '',
    src = ''
  } = props
  const [showModal, setShowModal] = useState(show)
  const [modalIndex, setModalIndex] = useState(0)
  const modalBodyRef = useRef<HTMLDivElement>(null)
  const { udo } = useUdoContext()
  const { themeClass } = useLobExperience()
  const tokenDescription = description ? getTokenValues(description, tokenProperties, udo?.localeName) : ''

  const flexOffers = useFlexOffers(ruleExpression)
  const renderChildrenWithReplacedTokens = (childElements: any) => {
    let propsWithReplacedToken
    try {
      if (childElements?.length > 0) {
        return childElements?.[0]?.map((child: any) => {
          // Replace tokens in child props here
          let newProps = { ...child.props }
          const stringifiedProps = newProps && JSON.stringify(newProps)
          propsWithReplacedToken = getNestedValue(stringifiedProps, flexOffers, modalIndex, false, true, true) as string
          newProps = propsWithReplacedToken && JSON.parse(propsWithReplacedToken)
          return cloneElement(child, newProps)
        })
      }
    } catch (error) {
      const errorDesc = `Error in rendering modal with bad character in ${propsWithReplacedToken}`
      console.error(errorDesc)
      triggerNewRelicAction({
        errorMessage: errorDesc,
        errorName: `Modal Error`,
        actionName: 'Modal Error'
      })
      return <Fallback isBanner={true} />
    }
  }

  const getModalBodyContent = () => {
    if (modalType === ModalTypes.video) {
      return <>{children}</>
    } else if (modalType === ModalTypes.custom && !isInEditor) {
      return <>{renderChildrenWithReplacedTokens(children)}</>
    } else if (modalType === ModalTypes.custom) {
      return <>{children}</>
    }
    return <></>
  }

  const ModalWithAEM = withAEM(Modal)

  const closeHandler = () => {
    if (handleClose) {
      handleClose()
    }
    clearLocalStorage('activeModalIndex')
    clearLocalStorage('activeModalExpression')
    setShowModal(false)
  }

  const handleOpen = (e: any) => {
    setModalIndex(e?.detail?.index)
    setLocalStorage('activeModalIndex', e?.detail?.index)
    setLocalStorage('activeModalExpression', e?.detail?.ruleExpression)
    setShowModal(true)
  }

  const getModalView = () => {
    if (isInEditor) {
      return (
        <div data-testid='react-modal-editor-view'>
          <div
            id={modalId}
            className={'basic-modal modal-editor-view ' + componentName + ' ' + themeClass}
            data-testid={`basic-modal-${modalId}`}
          >
            <ModalHeader {...props}></ModalHeader>
            <ModalBody {...props} isParsed={false}>
              {getModalBodyContent()}
            </ModalBody>
            <ModalFooter {...props}></ModalFooter>
          </div>
        </div>
      )
    } else {
      return (
        <ModalWithAEM
          {...props}
          id={modalId}
          show={showModal}
          data-testid='react-basic-modal'
          componentName={componentName}
          handleClose={closeHandler}
          primaryBtnText={pmrbtnText}
          primaryBtnLink={pmrbtnLink}
          primaryBtnClick={pmrbtnClick}
          secondaryBtnText={sndbtnText}
          secondaryBtnLink={sndbtnLink}
          secondaryBtnClick={sndbtnClick}
          description={tokenDescription}
          handleOpen={handleOpen}
          src={getDynamicMedia(src, DynamicMediaComponents.MODAL_BASIC, fileName)}
        >
          {!isWidget && (
            <div ref={modalBodyRef} style={{ display: 'none' }}>
              {getModalBodyContent()}
            </div>
          )}
          {getModalBodyContent()}
        </ModalWithAEM>
      )
    }
  }

  return <>{getModalView()}</>
}
export default ReactAuthorableModal
